import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/common/refactoredComponents/button/Button';
import Checkbox from 'src/common/refactoredComponents/checkbox/Checkbox';
import Spinner from 'src/common/refactoredComponents/spinner/Spinner';
import { ButtonStyles } from 'src/constants/buttonStyles';
import { isMobileView } from 'src/utils/util';
import { getTranslation } from './utils/util';
import { 
    MainContainer,
    MainContainerSecond,
    ActionsContainer,
    Title,
    TextArea,
    CustomText,
    ErrorLabel,
    CharactersRemainingText,
    SubmitContainer,
    ButtonDiv
} from '../index.styled';
import UploadImage from './UploadImage';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';
import { BASE_TEXT_PX, FONT_FAMILY_REGULAR, PRIMARY_FONT_COLOR, SMALL_TEXT_PX } from 'src/styles/fonts/MCFontConstants';

const ReportFormContent = ({
    issueDescription,
    setIssueDescription,
    attachedFiles,
    onDeleteFile,
    onViewFile,
    fileInput,
    showAttachLoader,
    fileValidationError,
    onFileChange,
    emailCopy,
    setEmailCopy,
    handleCancel,
    isSubmitting,
    onSubmit,
    japaneseTranslations,
    isLanguageChanged
}) => {
    const { t } = useTranslation();
    const isMobile = isMobileView();
    const isIphoneSE = isMobile && window.innerHeight < 700;

    const uploadAreaRef = useRef(null);
    const [uploadAreaHeight, setUploadAreaHeight] = useState(null);
    const submitAreaRef = useRef(null);
    const [isSubmitAreaVisible, setIsSubmitAreaVisible] = useState(true);

    const measureUploadArea = () => {
        const el = uploadAreaRef.current;
        if (!el) return;

        // Use offsetHeight to include padding/borders and to avoid fractional values
        const nextHeight = el.offsetHeight;
        if (nextHeight && nextHeight !== uploadAreaHeight) {
            setUploadAreaHeight(nextHeight);
        }
    };

    useLayoutEffect(() => {
        measureUploadArea(); 
    }, [attachedFiles?.length, showAttachLoader, fileValidationError]);

    useEffectWithErrorLogging(() => {
        const handleResize = () => measureUploadArea();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize); 
    }, []);

    useEffectWithErrorLogging(() => {
        const el = submitAreaRef.current;
        if (!el) return;

        const updateVisibility = () => {
            const rect = el.getBoundingClientRect();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            setIsSubmitAreaVisible(rect.top < viewportHeight);
        };

        updateVisibility();
        window.addEventListener('scroll', updateVisibility, { passive: true });

        return () => {
            window.removeEventListener('scroll', updateVisibility);
        };
    }, [attachedFiles?.length, showAttachLoader, fileValidationError, issueDescription]);

    return (isSubmitting? 
            <Spinner
                animation="border"
                variant="primary"
                small
                id="report-issue-loadingSpinner"
                marginTop='20%'
            ></Spinner> 
         :
        <MainContainer>
            <MainContainerSecond 
                padding='0px'
                boxSizing='border-box'
            >
                <Title>Report Issue</Title>
                <CustomText 
                    fontSize={BASE_TEXT_PX}
                    fontFamily={FONT_FAMILY_REGULAR}
                    color={PRIMARY_FONT_COLOR}
                    marginBottom='12px'
                >
                    {getTranslation(t, 'reportIssue.description', isLanguageChanged, japaneseTranslations)}
                </CustomText>
                <CharactersRemainingText>
                    {issueDescription?.length
                        ? (4000 - issueDescription.length < 0
                            ? 0
                            : 4000 - issueDescription.length)
                        : 4000
                    }{' '}{getTranslation(t, 'reportIssue.charactersRemainingLabel', isLanguageChanged, japaneseTranslations)}
                </CharactersRemainingText>
                <TextArea
                    id='report-issue-textarea'  
                    value={issueDescription}
                    onChange={(e) => setIssueDescription(e.target.value)}
                    placeholder={getTranslation(t, 'reportIssue.placeholder', isLanguageChanged, japaneseTranslations)}
                    maxLength={4000}
                    height={isMobile ?
                        isIphoneSE ? '152px !important': '262px !important' : '114px !important'}
                />
            </MainContainerSecond>
            <MainContainerSecond 
                padding='0px'
                boxSizing='border-box'
            >   
                <ActionsContainer isMobile={isMobile}>
                    <div>
                        <div ref={uploadAreaRef}>
                            <UploadImage 
                                attachedFiles={attachedFiles}
                                onDeleteFile={onDeleteFile}
                                onViewFile={onViewFile}
                                showAttachLoader={showAttachLoader}
                                japaneseTranslations={japaneseTranslations}
                                isLanguageChanged={isLanguageChanged}
                            />
                        </div>
                        {fileValidationError && (
                            <ErrorLabel>
                                {fileValidationError}
                            </ErrorLabel>
                        )}
                        <input
                            onChange={onFileChange}
                            id="file-upload"
                            type="file"
                            accept=".jpg,.jpeg,.gif,.tif,.tiff,.pdf,.bmp,.msg,.doc,.docx,.xls,.txt,.html,.png,.heic"
                            hidden
                            ref={fileInput}
                            multiple
                        />
                    </div>
                    <SubmitContainer
                        ref={submitAreaRef}
                        height=
                                {!isMobile && uploadAreaHeight && attachedFiles.length
                                    ? `${uploadAreaHeight + 20}px`
                                    : 'auto'}
                        marginBottom={
                            attachedFiles.length > 0
                                ? '200px'
                                : (isMobile && !isSubmitAreaVisible) 
                                ? '200px'
                                : (isIphoneSE || window.innerWidth<375)
                                ?'300px'
                                : '0'
                        }
                    >
                        <Checkbox
                            id='report-issue-checkbox'
                            width='12px'
                            height='12px'
                            label={getTranslation(t, 'reportIssue.emailMeACopy', isLanguageChanged, japaneseTranslations)}
                            margin='11px 9px'
                            justifyContent='flex-end'
                            checkboxBorder='1px solid #b1b1bf !important'
                            checkedBgColor='#b1b1bf'
                            labelStyleObj={{
                                fontFamily: FONT_FAMILY_REGULAR || 'sans-serif',
                                textAlign: 'left',
                                fontSize: SMALL_TEXT_PX,
                                letterSpacing: '-0.12px',
                                color: PRIMARY_FONT_COLOR,
                                margin: '5px 0 0 0',
                                opacity: 1, 
                            }}
                            checked={emailCopy}
                            onCheckboxSelectionChange={(e) => setEmailCopy(e.target.checked)}
                        />
                        <ButtonDiv 
                            isMobile={isMobile}
                            justifyContent='flex-end'
                            marginBottom={attachedFiles.length > 4 ? '100px' : '0'}
                        >
                            <Button
                                id='report-issue-cancel-button'
                                mobileWidth={'auto'}
                                height='38px' 
                                show={true}
                                variant={ButtonStyles.secondary}
                                clearNotesBg={true}
                                margin='5px 5px 0px 5px'
                                clicked={handleCancel}
                                fontSize={BASE_TEXT_PX}
                                padding='8px 13px'
                            >
                                {getTranslation(t, 'reportIssue.cancel', isLanguageChanged, japaneseTranslations, 'Cancel')}
                            </Button>
                            <Button
                                id='report-issue-submit-button'
                                show={true}
                                variant={issueDescription.trim() ? ButtonStyles.primary : ButtonStyles?.disabled}
                                height='38px'
                                mobileWidth='80px'
                                clicked={onSubmit}
                                margin='5px 0 0 5px'
                                fontSize={BASE_TEXT_PX}
                                disabled={!issueDescription.trim()}
                                padding='8px 12px'
                            >
                                {
                                    getTranslation(t, 'reportIssue.submit', isLanguageChanged, japaneseTranslations, 'Submit')
                                }
                            </Button>
                        </ButtonDiv>
                    </SubmitContainer>
                </ActionsContainer>
            </MainContainerSecond>
        </MainContainer>);
};

export default ReportFormContent;
